import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpUtils } from '../utils/httpUtils';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { QueryParam, ServiceUtil } from '../utils/serviceUtils';
import { ParticleCalibration, ParticleCalibrationFilter, ParticlesCalibrationAuditFilter } from '../model/particleCalibration';

@Injectable()
export class ParticleCalibrationService {

    constructor(private readonly http: HttpClient) {
    }

    findAll(filter: ParticleCalibrationFilter): Observable<any> {
        const url = `${environment.particlesUrl}/calibration/`;

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

     findAllTestType(): Observable<any> {
        const url = `${environment.particlesUrl}/calibration/testType`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }
    
    save(calibration: ParticleCalibration, token: string): Observable<any> {
        let url = `${environment.particlesUrl}/calibration/`;;
        const form = new FormData();
        form.append('calibration', JSON.stringify(calibration));
        form.append('token', token);
        
        if (calibration.id !== null && calibration.id !== undefined) {
            url += calibration.id;
            return this.http.put(url, { calibration, token }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            url += 'create';
            return this.http.post(url, { calibration, token }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        }
    }

     findOne(id: number): Observable<any> {
        const url = `${environment.particlesUrl}/calibration/${id}`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getAudit(id: number, filter: ParticlesCalibrationAuditFilter): Observable<any> {
        const url = `${environment.particlesUrl}/calibration/${id}/audit`;

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    findAllFromEquipment(id: number): Observable<any> {
        const url = environment.particlesUrl + `/calibration/equipment/${id}`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    findLastFromEquipment(id: number): Observable<any> {
        const url = environment.particlesUrl + `/calibration//equipment/${id}/last`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    manualSign(calibration: ParticleCalibration, document: File, token: string): Observable<any> {
        const url = environment.particlesUrl + `/calibration/${calibration.id}/manualSign`;

        const form = new FormData();
        form.append('document', document, document.name);
        form.append('calibration', JSON.stringify(calibration));
        form.append('token', token);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    automaticSign(idCalibration: number, token: string, lang?: string): Observable<any> {
        const url = environment.particlesUrl + `/calibration/${idCalibration}/automaticSign`;

        const form = new FormData();
        form.append('token', token);

        const headers = new HttpHeaders({ lang });

        return this.http.put(url, form, { headers }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    automaticSignBulk(ids: number[], token: string, lang?: string): Observable<any> {
        const url = environment.particlesUrl + '/calibration//bulk/automaticSign';

        const form = new FormData();
        form.append('ids', ids.join(';'));
        form.append('token', token);

        const headers = new HttpHeaders({ lang });

        return this.http.put(url, form, { headers }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadCertificates(ids: number[], lang: string): Observable<any> {
        const url = environment.particlesUrl + '/calibration//bulk/downloadCertificates';
        let headers = new HttpHeaders({});

        const form = new FormData();
        form.append('ids', ids.join(';'));

        if (lang != null) {
            headers = new HttpHeaders({ lang });
        }

        return this.http.post(url, form, { responseType: 'blob', headers });
    }

    delete(id: number): Observable<any> {
        const url = environment.particlesUrl + `/${id}`;

        return this.http.delete(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadPdf(id: number): Observable<any> {
        const url = environment.particlesUrl + `/report/calibration/try/${id}`;

        const lang: string =  'es';
        const headers = new HttpHeaders({ lang });

        const data = {
           
        };
        return this.http.post(url, data, { responseType: 'blob', headers });
    }

    downloadPdfCalibrationVersion(calibration: ParticleCalibration): Observable<any> {
        return this.downloadPdfVersion(calibration.id, calibration.signedVersion);
    }

    downloadPdfVersion(idCalibration: number, version: number): Observable<any> {
        const url = environment.particlesUrl + `/report/calibration/${idCalibration}/version/${version}`;

        return this.http.post(url, {}, { responseType: 'blob' });
    }

    downloadPdfLastVersion(idCalibration: number): Observable<any> {
        const url = environment.particlesUrl + `/${idCalibration}/version/latest/pdf`;

        return this.http.get(url, { responseType: 'blob' });
    }

    downloadAuditPdf(id: number): Observable<any> {
        const url = environment.particlesUrl + `/report/particles/${id}/audit`;

        return this.http.get(url, { responseType: 'blob' });
    }

    advance(calibration: ParticleCalibration, token: string): Observable<any> {
        const url = environment.particlesUrl + `/calibration/${calibration.id}/advance`;

        const form = new FormData();
        form.append('token', token);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    back(calibration: ParticleCalibration, token: string): Observable<any> {
        const url = environment.particlesUrl + `/calibration/${calibration.id}/back`;

        const form = new FormData();
        form.append('token', token);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    revertSign(calibration: ParticleCalibration, reason: string, token: string): Observable<any> {
        const url = environment.particlesUrl + `/calibration/${calibration.id}/revertSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('reason', reason);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    invalidate(id: number, token: string, reason: string): Observable<any> {
        const url = environment.particlesUrl + `/calibration/${id}/invalidate`;

        const form = new FormData();
        form.append('token', token);
        form.append('reason', reason);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    checkCopyToGroup(id: number, idGroup: number): Observable<any> {
        const url = environment.particlesUrl + `/calibration/clonate/${id}/toGroup/${idGroup}/check`;

        return this.http.post(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    copyToGroup(id: number, idGroup: number): Observable<any> {
        const url = environment.particlesUrl + `/calibration/clonate/${id}/toGroup/${idGroup}`;

        return this.http.put(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getExcelTemplate(): Observable<any> {
        const url = environment.particlesUrl + '/calibration/particles/excel-template';

        return this.http.get(url, { responseType: 'blob' });
    }

    checkCopyMasiveToGroup(ids: number[]): Observable<any> {
        const url = environment.particlesUrl + '/clonate/masive/toGroup/check';
        const params: QueryParam[] = [{key: 'ids', value: ids}]
        return this.http.get(ServiceUtil.createQueryParams(url, params)).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    copyMasiveToGroup(ids: number[]): Observable<any> {
        const url = environment.particlesUrl + '/clonate/masive/toGroup';
        return this.http.put(url, ids).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    copyToGroupWithFolder(id: number, idGroup: number, idFolder: number): Observable<any> {
        const url = environment.particlesUrl + `/clonate/${id}/toGroup/${idGroup}/folder/${idFolder}`;

        return this.http.put(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getAllFromEquipment(id: number): Observable<any> {
        const url = `${environment.particlesUrl}/calibration/equipment/${id}`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }
    
}
